import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { api } from '../api'
import { setTokenSource } from '../api/tokens'
import type { Me } from '../types'
import { Ctx } from './AuthContext'
import type { AuthState } from './AuthContext'
import { NO_COMPANY, signInProblem } from './provider'

/**
 * Signing in through Auth0 (STE-1012), behind the same `useAuth()` every screen
 * already uses, so no screen needs to know which sign-in is in use.
 *
 * Auth0's own pages do the signing in and out; no password passes through this
 * code. The SDK holds the session in memory and renews it silently through the
 * tenant's custom domain.
 */
export function Auth0AuthProvider({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, error, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0()
  const [me, setMe] = useState<Me | null>(null)
  const [reading, setReading] = useState(true)
  const [problem, setProblem] = useState('')

  // Every request carries the SDK's token; a refused one is renewed once.
  useEffect(() => {
    setTokenSource(isAuthenticated
      ? async (fresh) => (await getAccessTokenSilently(fresh ? { cacheMode: 'off' } : {})) ?? ''
      : null)
    return () => setTokenSource(null)
  }, [isAuthenticated, getAccessTokenSilently])

  const signOut = useCallback(async () => {
    setTokenSource(null)
    setMe(null)
    await logout({ logoutParams: { returnTo: window.location.origin } })
  }, [logout])

  // Who they are, from our own API, once Auth0 has signed them in.
  useEffect(() => {
    if (isLoading) return
    if (error) {
      const said = signInProblem({ error: (error as { error?: string }).error, error_description: error.message })
      setProblem(said)
      setReading(false)
      return
    }
    if (!isAuthenticated) { setMe(null); setReading(false); return }
    let live = true
    setReading(true)
    api.me()
      .then((m) => { if (live) { setMe(m); setProblem('') } })
      .catch((err) => {
        if (!live) return
        const problem = signInProblem(err)
        setProblem(problem)
        // A login with no company is signed out, so the next person at this
        // machine starts clean rather than half signed in.
        if (problem === NO_COMPANY) void logout({ logoutParams: { returnTo: window.location.origin } })
      })
      .finally(() => { if (live) setReading(false) })
    return () => { live = false }
  }, [isLoading, isAuthenticated, error, logout])

  const signIn = useCallback(async () => {
    setProblem('')
    // Auth0's own page, and back. Never its sign-up screen: only super admin adds people.
    await loginWithRedirect()
  }, [loginWithRedirect])

  const refresh = useCallback(async () => {
    try { setMe(await api.me()) } catch { setMe(null) }
  }, [])

  const noteRoleCount = useCallback((total: number) => {
    setMe((current) => (current && current.jdCount !== total ? { ...current, jdCount: total } : current))
  }, [])

  const value = useMemo<AuthState>(() => ({
    me,
    loading: isLoading || reading,
    signIn,
    // A new person sets their password from Auth0's email; there is no screen for it here.
    mustChoosePassword: null,
    choosePassword: async () => {},
    cancelPasswordChoice: () => {},
    signOut,
    refresh,
    noteRoleCount,
    isAdmin: me?.role === 'admin',
    isSuperAdmin: me?.role === 'superadmin',
    signInProblem: problem,
  }), [me, isLoading, reading, signIn, signOut, refresh, noteRoleCount, problem])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
